import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  ListGroup,
  Button,
  Card,
  Form,
  Collapse,
} from 'react-bootstrap';
import {
  FaTrash,
  FaShoppingCart,
  FaBoxes,
  FaMoneyBillWave,
  FaChartBar,
  FaTools,
  FaPlusCircle,
  FaBalanceScale,
} from 'react-icons/fa';
import Message from '../components/Message';
import { addToCart, removeFromCart } from '../slices/cartSlice';

const StoreAdminForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cartItems } = useSelector((state) => state.cart);

  const addToCartHandler = (product, qty) => {
    dispatch(addToCart({ ...product, qty }));
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  // ⭐ SECTIONS CONFIG (color-coded, with icons)
  const sectionsConfig = {
    sales: {
      title: 'Sales',
      color: '#0d6efd',
      Icon: FaShoppingCart,
      items: [
        { path: 'StoreAdmin/storeSalelist', label: 'Sales List' },
        { path: 'StoreAdmin/storeSupplylist', label: 'Supply List' },
      ],
    },
    stock: {
      title: 'Stock',
      color: '#198754',
      Icon: FaBoxes,
      items: [
        { path: 'StoreAdmin/storeStocklist', label: 'Stock List' },
        { path: 'StoreAdmin/dailystoreStocklist', label: 'Daily Stock List' },
        { path: 'StoreAdmin/openingstoreStocklist', label: 'Opening Stock List' },
        { path: 'StoreAdmin/DailyStoreStockAnalysis', label: 'Daily Stock Analysis' },
        { path: 'StoreAdmin/DailyStoreStockReport', label: 'Daily Stock Report' },
        { path: 'StoreAdmin/DailyStoreStockUniqueDates', label: 'Daily Stock Dates' },
        { path: 'StoreAdmin/ReportStoreStockScreen', label: 'Stock Report Screen' },
        { path: 'StoreAdmin/StoreStockAnalysis', label: 'Stock Analysis' },
        { path: 'StoreAdmin/StoreStockReport', label: 'Stock Report' },
        { path: 'StoreAdmin/StoreStockUniqueDates', label: 'Stock Unique Dates' },
      ],
    },
    expenses: {
      title: 'Expenses & Income',
      color: '#6f42c1',
      Icon: FaMoneyBillWave,
      items: [
        { path: 'storeAdmin/storeExpenselist', label: 'Expense List' },
        { path: 'storeAdmin/NetStoreCash', label: 'Cash Collected' },
        { path: 'StoreAdmin/storeExpensecategory', label: 'Expense Category' },
        { path: 'StoreAdmin/storeExpenseCategories', label: 'Expense Categories' },
        { path: 'StoreAdmin/storeIncomecategory', label: 'Income Category' },
        { path: 'StoreAdmin/storeworkingcapital', label: 'Working Capital' },
        { path: 'StoreAdmin/storeExpenseinvoices', label: 'Expense Invoices' },
        { path: 'StoreAdmin/storeExpenseinvoicesummary', label: 'Expense Invoice Summary' },
        { path: 'StoreAdmin/StockDateStoreExpenses', label: 'Stock Date Expenses' },
      ],
    },
    reports: {
      title: 'Reports & Totals',
      color: '#fd7e14',
      Icon: FaChartBar,
      items: [
        { path: 'StoreAdmin/storeSupplysummary', label: 'Supply Summary' },
        { path: 'StoreAdmin/storeSupplyinvoicesummary', label: 'Supply Invoice Summary' },
        { path: 'StoreAdmin/StoreAllTotal', label: 'Store All Total' },
      ],
    },
    bulk: {
      title: 'Bulk Tools & Search/Replace',
      color: '#dc3545',
      Icon: FaTools,
      items: [
        { path: 'StoreAdmin/storeStockbulkeditscreen', label: 'Stock Bulk Edit' },
        { path: 'update/storeIncome/excelform', label: 'Bulk Income Excel' },
        { path: 'update/storeExpense/excelform', label: 'Bulk Expense Excel' },
        { path: 'bulk/update/storeExpense', label: 'Batch Expense Update' },
        { path: 'StoreAdmin/storeExpensesearchreplace', label: 'Expense Search/Replace' },
        { path: 'StoreAdmin/storeStocknotesearchreplace', label: 'Stock Note Search/Replace' },
        { path: 'StoreAdmin/storeStocksearchreplace', label: 'Stock Search/Replace' },
        { path: 'StoreAdmin/storeTransfersearchreplace', label: 'Transfer Search/Replace' },
        { path: 'StoreAdmin/storeSupplysearchreplace', label: 'Supply Search/Replace' },
        { path: 'StoreAdmin/storeSalesearchreplace', label: 'Sales Search/Replace' },
      ],
    },
    add: {
      title: 'Add / Create',
      color: '#ffc107',
      Icon: FaPlusCircle,
      items: [
        { path: 'create/staff/storeDeposit', label: 'Add Staff Deposit' },
        { path: 'create/staff/storeExpense', label: 'Add Staff Expense' },
        { path: 'create/staff/storeIncome', label: 'Add Staff Income' },
        { path: 'storeAdmin/AddStoreDepositScreen', label: 'Add Deposit' },
        { path: 'storeAdmin/AddStoreExpenseFromExcelScreen', label: 'Add Expense (Excel)' },
        { path: 'storeAdmin/AddStoreExpenseScreen', label: 'Add Expense' },
        { path: 'storeAdmin/AddStoreIncomeFromExcelScreen', label: 'Add Income (Excel)' },
        { path: 'storeAdmin/AddStoreIncomeScreen', label: 'Add Income' },
      ],
    },
    balance: {
      title: 'Balance & Deposits',
      color: '#343a40',
      Icon: FaBalanceScale,
      items: [
        { path: 'StoreAdmin/storedepositlist', label: 'Deposit List' },
        { path: 'storeAdmin/Balance', label: 'Balance' },
        { path: 'storeAdmin/BalanceByCustomer', label: 'Balance by Customer' },
        { path: 'storeAdmin/BalanceByCustomerStaff', label: 'Balance by Customer Staff' },
        { path: 'storeAdmin/BalanceByUser', label: 'Balance by User' },
        { path: 'storeAdmin/BalanceByUserStaff', label: 'Balance by User Staff' },
        { path: 'storeAdmin/BalanceStaffScreen', label: 'Balance Staff' },
      ],
    },
  };

  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    Object.keys(sectionsConfig).forEach((key) => {
      initial[key] = true;
    });
    return initial;
  });

  const [searchTerm, setSearchTerm] = useState('');

  const navHandler = (path) => {
    navigate(`/login?redirect=/${path}`);
  };

  const handleToggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filterItems = (items) => {
    if (!normalizedSearch) return items;
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(normalizedSearch) ||
        item.path.toLowerCase().includes(normalizedSearch)
    );
  };

  return (
    <Row>
      {/* LEFT PANEL */}
      <Col md={8}>
        <h1 style={{ marginBottom: '20px' }}>Browse Mini Pages</h1>

        {cartItems.length === 0 ? (
          <Message>
            Go <Link to="/house/HomeScreen">Back</Link>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row>
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Col>

                  <Col md={3}>
                    <Link to={`/product/${item._id}`}>{item.name}</Link>
                  </Col>

                  <Col md={2}>${item.price}</Col>

                  <Col md={2}>
                    <Button
                      type="button"
                      variant="light"
                      onClick={() =>
                        addToCartHandler(item, item.qty > 1 ? item.qty - 1 : 1)
                      }
                      disabled={item.qty === 1}
                    >
                      -
                    </Button>
                    <span className="mx-2">{item.qty}</span>
                    <Button
                      type="button"
                      variant="light"
                      onClick={() => addToCartHandler(item, item.qty + 1)}
                      disabled={item.qty === item.countInStock}
                    >
                      +
                    </Button>
                  </Col>

                  <Col md={2}>${item.price * item.qty}</Col>

                  <Col md={1}>
                    <Button
                      type="button"
                      variant="light"
                      onClick={() => removeFromCartHandler(item._id)}
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* RIGHT PANEL */}
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)}) items
              </h2>
              $
              {cartItems
                .reduce((acc, item) => acc + item.qty * item.price, 0)
                .toFixed(2)}
            </ListGroup.Item>

            {/* SEARCH BAR */}
            <ListGroup.Item>
              <Form.Control
                type="text"
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </ListGroup.Item>

            {/* COLLAPSIBLE SECTIONS */}
            {Object.entries(sectionsConfig).map(([key, section]) => {
              const { title, color, Icon, items } = section;
              const filteredItems = filterItems(items);

              if (filteredItems.length === 0) return null;

              return (
                <ListGroup.Item key={key} className="p-0">
                  <Card className="mb-2">
                    <Card.Header
                      onClick={() => handleToggleSection(key)}
                      style={{
                        backgroundColor: color,
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon />
                        {title}
                      </span>
                      <span>{openSections[key] ? '−' : '+'}</span>
                    </Card.Header>
                    <Collapse in={openSections[key]}>
                      <div>
                        <Card.Body>
                          <Row>
                            {filteredItems.map((item) => (
                              <Col md={6} key={item.path} className="mb-2">
                                <Button
                                  type="button"
                                  className="w-100"
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    color: '#000',
                                    borderColor: color,
                                    borderWidth: '2px',
                                    textAlign: 'left',
                                  }}
                                  onClick={() => navHandler(item.path)}
                                >
                                  {item.label}
                                </Button>
                              </Col>
                            ))}
                          </Row>
                        </Card.Body>
                      </div>
                    </Collapse>
                  </Card>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default StoreAdminForm;
